import type { ConfigurationOperation } from '@festina/configuration-general-types/configuration'
import {
  configurationKeys,
  type Tree,
  type WorkspaceKey,
} from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { useQueryClient } from '@tanstack/react-query'

import { useNavigationParams } from '../../system/useNavigationParams'
import { useOperationsContext } from '../../workspace/operationsContext'
import { getOperationTypeForAnnulment } from '../annulOperations/helpers'
import type { OperationWithSourceInfo } from '../types'
import type { ChangeOperation } from '../workspace/types'
import { getHighestLayerKey } from './getHighestLayerKey'

export type TransferProps<T extends ConfigurationOperation> = {
  shouldInvalidateCacheAfterTransfer?: boolean
  sourceBlock: string
  destinationBlock: string
  operationName: string
  operations: OperationWithSourceInfo<T>[]
  layerTopology: Optional<Tree>
}

type UseTransferOperationResult<T extends ConfigurationOperation> = {
  onTransfer: (props: TransferProps<T>) => Promise<Optional<WorkspaceKey>>
}

export const useTransferOperation = <
  T extends ConfigurationOperation,
>(): UseTransferOperationResult<T> => {
  const { onAddOrUpdateOperation } = useOperationsContext()
  const translate = useTranslate()
  const queryClient = useQueryClient()
  const { server } = useNavigationParams()
  const onTransfer = async <T extends ConfigurationOperation>({
    shouldInvalidateCacheAfterTransfer,
    sourceBlock,
    destinationBlock,
    operationName,
    operations,
    layerTopology,
  }: TransferProps<T>) => {
    const nonAnnulledOperations = operations.filter(
      ({ annulOperations }) => !annulOperations.length,
    )
    const firstOperation = nonAnnulledOperations.at(0)

    if (!firstOperation) {
      return
    }

    for (const createOp of nonAnnulledOperations) {
      const internalOperation = createOp.internalOperation
      await onAddOrUpdateOperation({
        operation: {
          ...internalOperation,
          operationKey: undefined,
        },
        layer: createOp.layer,
        block: destinationBlock,
        shouldOpenUnifiedBranchWorkspace: true,
      })
    }

    const annulmentLayer = getHighestLayerKey(
      layerTopology,
      nonAnnulledOperations.map(({ layer }) => layer),
    )
    const workspaceKey = await onAddOrUpdateOperation({
      operation: {
        _type: 'AnnulOperation',
        createOperationClassName: getOperationTypeForAnnulment(
          firstOperation.internalOperation,
        ),
        name: operationName,
        reason: translate('transfer.annulmentReason', {
          sourceBlock,
          destinationBlock,
        }),
      } as ChangeOperation,
      layer: annulmentLayer,
      block: sourceBlock,
      shouldOpenUnifiedBranchWorkspace: true,
    })

    if (shouldInvalidateCacheAfterTransfer) {
      // It's best to invalidate all workspace operations given that writing many operations so closely to each other can lead
      // to race conditions with optimistic updates.
      void queryClient.invalidateQueries({
        queryKey: configurationKeys.fetchWorkspaceOperationsKey({
          workspaceName: workspaceKey.name,
          server,
        }),
      })
    }

    return workspaceKey
  }

  return { onTransfer }
}
